/* eslint-disable jsx-a11y/anchor-is-valid */
import { Button, Card, Label, TextInput } from "flowbite-react";
import type { FC } from "react";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authService } from "../../services/main";
import toast from "react-hot-toast";

const ResetPasswordPage: FC = function () {
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      toast.error("Invalid or missing reset token. Please request a new password reset.");
      navigate("/authentication/forgot-password");
    }
  }, [token, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      toast.error("Invalid reset token");
      return;
    }

    if (!newPassword || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      await authService.resetPassword(token, newPassword);
      setSuccess(true);
      toast.success("Password reset successful!");
      
      // Redirect to sign in after 2 seconds
      setTimeout(() => {
        navigate("/authentication/sign-in");
      }, 2000);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "An unexpected error occurred.";
      toast.error(`Password reset failed: ${errorMessage}`);
      
      // If token is invalid or expired, redirect to forgot password page
      if (errorMessage.includes("token") || errorMessage.includes("expired") || errorMessage.includes("Invalid")) {
        setTimeout(() => {
          navigate("/authentication/forgot-password");
        }, 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return null; // Will redirect in useEffect
  }

  if (success) {
    return (
      <div className="flex flex-col items-center justify-center px-6 lg:h-screen lg:gap-y-12">
        <a href="/" className="my-6 flex items-center gap-x-1 lg:my-0">
          <img
            alt="CRM Platform logo"
            src="https://stage.vidzai.com/ops/images/logo.png"
            className="mr-3 h-10"
          />
          <span className="self-center whitespace-nowrap text-2xl font-semibold dark:text-white">
            CRM Platform
          </span>
        </a>
        <Card className="w-full lg:max-w-[640px] lg:[&>*]:w-full lg:[&>*]:p-16">
          <div className="text-center">
            <div className="mb-4">
              <svg
                className="mx-auto h-12 w-12 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h1 className="mb-3 text-2xl font-bold dark:text-white md:text-3xl">
              Password Reset Successful!
            </h1>
            <p className="mb-6 text-gray-500 dark:text-gray-300">
              Your password has been reset successfully. You will be redirected to the sign-in page shortly.
            </p>
            <Button
              onClick={() => navigate("/authentication/sign-in")}
              className="w-full lg:w-auto"
            >
              Go to Sign In
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center px-6 lg:h-screen lg:gap-y-12">
      <a href="/" className="my-6 flex items-center gap-x-1 lg:my-0">
        <img
          alt="CRM Platform logo"
          src="https://stage.vidzai.com/ops/images/logo.png"
          className="mr-3 h-10"
        />
        <span className="self-center whitespace-nowrap text-2xl font-semibold dark:text-white">
          CRM Platform
        </span>
      </a>
      <Card className="w-full lg:max-w-[640px] lg:[&>*]:w-full lg:[&>*]:p-16">
        <h1 className="mb-3 text-2xl font-bold dark:text-white md:text-3xl">
          Reset your password
        </h1>
        <p className="mb-6 text-gray-500 dark:text-gray-300">
          Enter your new password below. Make sure it's at least 6 characters long.
        </p>
        <form onSubmit={handleSubmit}>
          <div className="mb-6 flex flex-col gap-y-3">
            <Label htmlFor="newPassword">New password</Label>
            <TextInput
              id="newPassword"
              name="newPassword"
              placeholder="••••••••"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              disabled={loading}
              minLength={6}
            />
          </div>
          <div className="mb-6 flex flex-col gap-y-3">
            <Label htmlFor="confirmNewPassword">Confirm New Password</Label>
            <TextInput
              id="confirmNewPassword"
              name="confirmNewPassword"
              placeholder="••••••••"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={loading}
              minLength={6}
            />
          </div>
          <div className="mb-6">
            <Button 
              type="submit" 
              className="w-full lg:w-auto"
              disabled={loading}
            >
              {loading ? "Resetting..." : "Reset password"}
            </Button>
          </div>
        </form>
        <p className="text-sm text-gray-500 dark:text-gray-300">
          Remember your password?&nbsp;
          <button
            onClick={() => navigate("/authentication/sign-in")}
            className="text-primary-600 dark:text-primary-300 hover:underline"
          >
            Sign in
          </button>
        </p>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
